import * as React from "react";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Input,
  Sheet,
  Stack,
  Switch,
  Table,
  Tooltip,
  Typography,
} from "@mui/joy";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PushPinRoundedIcon from "@mui/icons-material/PushPinRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import { Link as RouterLink } from "react-router-dom";

import {
  fetchAdminReadingNotes,
  searchAdminReadingNotes,
  updateAdminReadingNotePin,
  ReadingNote,
} from "../../api";

interface AdminReadingNotesListProps {
  refreshKey?: number;
}

export default function AdminReadingNotesList({ refreshKey }: AdminReadingNotesListProps) {
  const [notes, setNotes] = React.useState<ReadingNote[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const deferredQuery = React.useDeferredValue(query.trim());
  const refreshMountedRef = React.useRef(false);
  const pinnedCount = React.useMemo(() => notes.filter((note) => note.pinned).length, [notes]);
  const noteSummary = React.useMemo(() => {
    const total = notes.length;
    const pinned = pinnedCount;
    return { total, pinned };
  }, [notes, pinnedCount]);

  const loadNotes = React.useCallback(async (searchTerm?: string) => {
    setLoading(true);
    const load = searchTerm ? searchAdminReadingNotes(searchTerm) : fetchAdminReadingNotes();
    try {
      const data = await load;
      setNotes(data ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadNotes(deferredQuery.length > 0 ? deferredQuery : undefined);
  }, [deferredQuery, loadNotes]);

  React.useEffect(() => {
    if (!refreshMountedRef.current) {
      refreshMountedRef.current = true;
      return;
    }
    if (typeof refreshKey !== "number") return;

    setIsRefreshing(true);
    loadNotes(deferredQuery.length > 0 ? deferredQuery : undefined).finally(() => setIsRefreshing(false));
  }, [refreshKey, deferredQuery, loadNotes]);

  const handlePinnedChange = async (note: ReadingNote, pinned: boolean) => {
    await updateAdminReadingNotePin(note.id, {
      pinned,
      order: pinned ? notes.filter((n) => n.pinned).length + 1 : undefined,
    });
    await loadNotes(deferredQuery.length > 0 ? deferredQuery : undefined);
  };

  const [dragIndex, setDragIndex] = React.useState<number | null>(null);

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDrop = async (index: number) => {
    if (dragIndex === null || dragIndex === index) return;
    const updated = [...notes];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(index, 0, moved);
    setNotes(updated);
    const pinned = updated.filter((n) => n.pinned);
    for (let i = 0; i < pinned.length; i++) {
      const n = pinned[i];
      await updateAdminReadingNotePin(n.id, { pinned: true, order: i + 1 });
      n.order = i + 1;
    }
    setDragIndex(null);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "xl",
        p: { xs: 2.5, md: 3 },
        gap: 2.5,
        boxShadow: "sm",
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Sheet
              variant="solid"
              color="primary"
              sx={{
                width: 44,
                height: 44,
                display: "grid",
                placeItems: "center",
                borderRadius: "lg",
                boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.3)",
              }}
            >
              <MenuBookRoundedIcon fontSize="small" />
            </Sheet>
            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
              <Typography level="title-lg" sx={{ fontWeight: 700 }}>
                Manage article library
              </Typography>
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                Reorder pinned wisdom, surface fresh reflections, and ensure seekers always find guidance.
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="sm"
              variant="soft"
              color="primary"
              startDecorator={<PushPinRoundedIcon fontSize="small" />}
            >
              {pinnedCount} pinned
            </Chip>
            <Divider orientation="vertical" sx={{ height: 28, display: { xs: "none", sm: "block" } }} />
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              Drag pinned articles to reprioritize.
            </Typography>
          </Stack>
        </Stack>

        <Sheet
          variant="soft"
          sx={{
            borderRadius: "lg",
            p: 1.5,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.level1",
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
            <Input
              size="sm"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by title or reflection"
              startDecorator={<SearchRoundedIcon fontSize="small" />}
              sx={{
                borderRadius: "lg",
                boxShadow: "inset 0 1px 0 rgba(15,23,42,0.08)",
                maxWidth: { xs: "100%", md: 360 },
                bgcolor: "background.surface",
              }}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip size="sm" variant="soft" color="primary">
                {noteSummary.total} total
              </Chip>
              <Chip size="sm" variant="soft" color="neutral">
                {noteSummary.pinned} pinned
              </Chip>
            </Stack>
          </Stack>
        </Sheet>
      </Stack>

      {isRefreshing ? (
        <Alert variant="soft" color="primary" sx={{ borderRadius: "lg" }}>
          Refreshing your article list…
        </Alert>
      ) : null}

      <Sheet
        variant="outlined"
        sx={{
          borderRadius: "lg",
          maxHeight: { xs: "none", lg: 520 },
          overflowX: "auto",
          overflowY: "auto",
          borderColor: "divider",
        }}
      >
        {loading ? (
          <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ py: 8 }}>
            <CircularProgress size="sm" />
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              Gathering devotional readings…
            </Typography>
          </Stack>
        ) : notes.length === 0 ? (
          <Stack spacing={1.5} alignItems="center" sx={{ py: 8, textAlign: "center", px: 3 }}>
            <Avatar
              variant="outlined"
              color="primary"
              sx={{
                width: 56,
                height: 56,
                borderRadius: "xl",
                display: "grid",
                placeItems: "center",
                boxShadow: "0 12px 24px rgba(13,109,219,0.18)",
              }}
            >
              <ArticleRoundedIcon />
            </Avatar>
            <Typography level="title-md" sx={{ fontWeight: 700 }}>
              No articles yet
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 360 }}>
              Your first reflection will light the way for many. Compose a heartfelt note and pin it to welcome seekers.
            </Typography>
            <Button
              size="sm"
              variant="solid"
              color="primary"
              component={RouterLink}
              to="/articles/new"
              startDecorator={<AddRoundedIcon />}
            >
              Create an article
            </Button>
          </Stack>
        ) : (
          <Table
            hoverRow
            stickyHeader
            borderAxis="bothBetween"
            size="sm"
            sx={{
              "--TableCell-headBackground": "var(--joy-palette-background-level1)",
              "--TableCell-paddingX": "16px",
              "--TableCell-paddingY": "12px",
              minWidth: 720,
              "& th": {
                fontSize: "0.72rem",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "text.tertiary",
                fontWeight: 700,
              },
              "& td": {
                fontSize: "0.86rem",
              },
            }}
          >
            <thead>
              <tr>
                <th style={{ width: 400 }}>Title</th>
                <th style={{ width: 150 }}>Pinned</th>
                <th style={{ width: 100 }}>Order</th>
                <th style={{ width: 150 }}>Created</th>
                <th style={{ width: 100 }} aria-label="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {notes.map((note, index) => {
                const plainContent = note.content.replace(/<[^>]+>/g, "").trim();
                const preview = plainContent.slice(0, 96);
                const showEllipsis = plainContent.length > 96;

                return (
                  <tr
                    key={note.id}
                    draggable={note.pinned}
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => handleDrop(index)}
                    style={{ cursor: note.pinned ? "grab" : "default" }}
                  >
                    <td>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {note.pinned ? (
                          <Tooltip title="Drag to reprioritize">
                            <IconButton size="sm" variant="plain" color="neutral">
                              <DragIndicatorIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <IconButton size="sm" variant="soft" color="neutral" disabled sx={{ visibility: "hidden" }}>
                            <DragIndicatorIcon />
                          </IconButton>
                        )}
                        <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                          <Typography level="body-md" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {note.title}
                          </Typography>
                          <Typography level="body-sm" sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {preview || "Tap to explore the full reflection."}
                            {showEllipsis ? "…" : ""}
                          </Typography>
                        </Stack>
                      </Stack>
                    </td>
                    <td>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Switch
                          size="sm"
                          checked={note.pinned}
                          onChange={(event) => handlePinnedChange(note, event.target.checked)}
                        />
                        {note.pinned ? (
                          <Chip size="sm" variant="soft" color="primary">
                            Highlighted
                          </Chip>
                        ) : null}
                      </Stack>
                    </td>
                    <td>
                      <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                        {note.pinned ? note.order ?? "—" : "—"}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                        {new Date(note.createdAt).toLocaleDateString()}
                      </Typography>
                    </td>
                    <td>
                      <Button
                        component={RouterLink}
                        to={`/articles/${note.id}`}
                        size="sm"
                        variant="outlined"
                        color="neutral"
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Sheet>
    </Card>
  );
}
